/**
 * useFontConfig Hook
 *
 * React hook for managing project-level font configuration.
 * Provides real-time subscription and save operations.
 *
 * Follows the same pattern as useAnalyticsSettings.
 *
 * @module font-config/hooks
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ProjectFontConfig, PlatformId } from '../types/fontConfig.types';
import { DEFAULT_FONT_CONFIG } from '../types/fontConfig.types';
import { getFontConfigService } from '../services/FontConfigService';
import { DEFAULT_PLATFORM_MAPPINGS } from '../data/systemFontCatalog';

export interface UseFontConfigResult {
  /** Current font configuration */
  config: ProjectFontConfig;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Saving state */
  saving: boolean;

  /**
   * Get the effective font mapping for a schema font + platform.
   * Returns user override if set, otherwise default.
   */
  getMapping: (schemaFont: string, platform: PlatformId) => string;

  /**
   * Set a font mapping for a schema font + platform.
   * Saves to Firestore immediately.
   */
  setMapping: (schemaFont: string, platform: PlatformId, fontName: string) => Promise<void>;

  /**
   * Set the SAME target font for a slot on every platform in ONE save
   * ("apply to all platforms" — the natural shape for web fonts, which
   * render identically everywhere). Platform values equal to their default
   * are stored as no-override, like setMapping.
   */
  setMappingForAllPlatforms: (schemaFont: string, fontName: string) => Promise<void>;

  /**
   * Get the display label for a font slot.
   * Returns user-defined label if set, otherwise the font name itself.
   */
  getFontLabel: (schemaFont: string) => string;

  /**
   * Set a display label for a font slot.
   * Saves to Firestore immediately. Pass empty string to reset to default (removes override).
   */
  setFontLabel: (schemaFont: string, label: string) => Promise<void>;

  /**
   * Reset all mappings and labels to defaults.
   */
  resetToDefaults: () => Promise<void>;
}

/**
 * Hook for managing project font configuration
 *
 * @param projectId - The project ID to manage font config for
 */
export function useFontConfig(projectId: string | undefined): UseFontConfigResult {
  const [config, setConfig] = useState<ProjectFontConfig>({ ...DEFAULT_FONT_CONFIG });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const service = useMemo(() => getFontConfigService(), []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!projectId) {
      setConfig({ ...DEFAULT_FONT_CONFIG });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = service.subscribeToConfig(projectId, (newConfig) => {
      setConfig(newConfig);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [projectId, service]);

  // Get effective mapping (user override or default)
  const getMapping = useCallback(
    (schemaFont: string, platform: PlatformId): string => {
      return (
        config.platformMappings?.[schemaFont]?.[platform] ??
        DEFAULT_PLATFORM_MAPPINGS[schemaFont]?.[platform] ??
        schemaFont
      );
    },
    [config.platformMappings],
  );

  // Set a single mapping and save
  const setMapping = useCallback(
    async (schemaFont: string, platform: PlatformId, fontName: string) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        const defaultFont = DEFAULT_PLATFORM_MAPPINGS[schemaFont]?.[platform];
        const newMappings = { ...config.platformMappings };

        if (fontName === defaultFont) {
          // Remove override if it matches the default
          if (newMappings[schemaFont]) {
            const { [platform]: _, ...rest } = newMappings[schemaFont];
            if (Object.keys(rest).length === 0) {
              delete newMappings[schemaFont];
            } else {
              newMappings[schemaFont] = rest;
            }
          }
        } else {
          // Set override
          newMappings[schemaFont] = {
            ...newMappings[schemaFont],
            [platform]: fontName,
          };
        }

        const newConfig: ProjectFontConfig = {
          ...config,
          platformMappings: newMappings,
        };

        await service.saveConfig(projectId, newConfig);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to save font config'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, config, service],
  );

  // Apply one target font to every platform of a slot in a single save.
  // A sequential setMapping loop would NOT work here: each call closes over
  // the same stale `config` state, so later saves clobber earlier platforms.
  const setMappingForAllPlatforms = useCallback(
    async (schemaFont: string, fontName: string) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        const newMappings = { ...config.platformMappings };
        const defaults = DEFAULT_PLATFORM_MAPPINGS[schemaFont] ?? {};
        const platforms = Object.keys(defaults) as PlatformId[];

        const rowOverrides: Partial<Record<PlatformId, string>> = {};
        for (const platform of platforms) {
          if (fontName !== defaults[platform]) {
            rowOverrides[platform] = fontName;
          }
        }

        if (Object.keys(rowOverrides).length === 0) {
          delete newMappings[schemaFont];
        } else {
          newMappings[schemaFont] = rowOverrides;
        }

        const newConfig: ProjectFontConfig = {
          ...config,
          platformMappings: newMappings,
        };

        await service.saveConfig(projectId, newConfig);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to save font config'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, config, service],
  );

  // Get display label (user override or font name)
  const getFontLabel = useCallback(
    (schemaFont: string): string => {
      return config.fontLabels?.[schemaFont] || schemaFont;
    },
    [config.fontLabels],
  );

  // Set a display label and save
  const setFontLabel = useCallback(
    async (schemaFont: string, label: string) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        const newLabels = { ...config.fontLabels };

        if (!label || label === schemaFont) {
          // Remove override if empty or matches font name (= default)
          delete newLabels[schemaFont];
        } else {
          newLabels[schemaFont] = label;
        }

        const newConfig: ProjectFontConfig = {
          ...config,
          fontLabels: Object.keys(newLabels).length > 0 ? newLabels : undefined,
        };

        await service.saveConfig(projectId, newConfig);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to save font label'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, config, service],
  );

  // Reset to defaults
  const resetToDefaults = useCallback(async () => {
    if (!projectId) throw new Error('No project ID');

    setSaving(true);
    try {
      await service.saveConfig(projectId, {
        version: 1,
        platformMappings: {},
        updatedAt: '',
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to reset font config'));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [projectId, service]);

  return {
    config,
    loading,
    error,
    saving,
    getMapping,
    setMapping,
    setMappingForAllPlatforms,
    getFontLabel,
    setFontLabel,
    resetToDefaults,
  };
}
